import React, { useState } from "react"
import { graphql } from "gatsby"
import Image from "gatsby-image"

import Layout from "../components/layout"
import SEO from "../components/seo"

const LightboxImage = ({ localFile, sizes }) => (
  <a
    className="with-image"
    href={localFile.childImageSharp.fluid.src}
    data-lightbox="main"
  >
    <Image
      fluid={
        sizes
          ? { ...localFile.childImageSharp.fluid, sizes }
          : localFile.childImageSharp.fluid
      }
    />
  </a>
)

const PropertyTemplate = ({ data, pageContext }) => {
  const whatsappMessage = data.propertyWhatsappContactText
    ? data.propertyWhatsappContactText.replace("{url}", pageContext.pageUrl)
    : ""
  const whatsappHref = `https://wa.me/34637889111${
    whatsappMessage ? `?text=${encodeURI(whatsappMessage)}` : ""
  }`
  const phoneHref = "tel:+34637889111"
  const emailAddress = "patricia@patricia.properties"
  const emailSubject = [
    data.airtable.data.Reference,
    data.airtable.data.TitleTrans,
  ]
    .filter(Boolean)
    .join(" — ")
  const emailHref = `mailto:${emailAddress}${
    emailSubject ? `?subject=${encodeURIComponent(emailSubject)}` : ""
  }`
  const propertyRef = data.airtable.data.Reference || ""
  const whatsappLabel =
    data.contactWhatsappText ||
    (pageContext.lang === "es" ? "Escríbeme" : "Message me")
  const whatsappLabelMobile =
    data.contactWhatsappMobileText ||
    (pageContext.lang === "es" ? "Escríbenos" : "Message us")
  const trackClass = method =>
    `plausible-event-name=Property+Contact plausible-event-method=${method}${
      propertyRef ? ` plausible-event-ref=${propertyRef}` : ""
    }`
  const emailLabel = data.contactEmailText || emailAddress
  const emailCopiedLabel =
    pageContext.lang === "es" ? "¡Email copiado!" : "Email copied!"
  const [emailCopied, setEmailCopied] = useState(false)
  const copyEmail = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(emailAddress)
      setEmailCopied(true)
      setTimeout(() => setEmailCopied(false), 2000)
    }
  }

  return (
    <Layout
      lang={pageContext.lang}
      alternateLang={pageContext.alternateLang}
    >
      <SEO
        lang={pageContext.lang}
        path={pageContext.pageUrl}
        title={`${data.airtable.data.TitleTrans}, ${data.airtable.data.Location}`}
        description={`${data.airtable.fields.subtitleTrans}. ${
          data.airtable.data.BedroomsTrans
        }${
          data.airtable.data.BedroomsTrans && data.airtable.data.Surface
            ? " — "
            : ""
        }${
          data.airtable.data.Surface
            ? `${data.airtable.data.SurfaceTrans} m²`
            : ""
        }.`}
        image={
          data.airtable.data.FeaturedImage &&
          data.airtable.data.FeaturedImage.childImageSharp
            ? data.airtable.data.FeaturedImage.childImageSharp.resize.src
            : null
        }
      />
      <div id="header" className="for-proj">
        <div className="section-content">
          <div className="big-text">
            <h1 className="t-left f-large">
              {!!data.airtable.data.BannerTrans && (
                <span className="properties-banner-inline">
                  {data.airtable.data.BannerTrans}{" "}
                </span>
              )}
              {data.airtable.data.TitleTrans}
            </h1>
            <div className="separator-small"></div>
            <h2 className="t-left f-medium normal">
              {data.airtable.fields.subtitleTrans}
            </h2>
            <ul className="t-left f-medium normal">
              {data.airtable.data.BedroomsTrans ||
              data.airtable.data.Surface ? (
                <li>
                  {data.airtable.data.BedroomsTrans}
                  {data.airtable.data.BedroomsTrans &&
                  data.airtable.data.Surface
                    ? " — "
                    : ""}
                  {data.airtable.data.Surface
                    ? `${data.airtable.data.SurfaceTrans} m²`
                    : ""}
                </li>
              ) : null}
              {data.airtable.data.PriceTrans ? (
                <li className="extrabold">{data.airtable.data.PriceTrans} €</li>
              ) : null}
            </ul>
          </div>
        </div>
      </div>
      <div id="project">
        <div className="section-content section-content-wide-tablet">
          <div className="brief no-margin position-relative property-hero">
            {data.airtable.data.Images.localFiles.length ? (
              <LightboxImage
                localFile={
                  data.airtable.data.Images.localFiles[
                    data.airtable.data.Images.localFiles.length - 1
                  ]
                }
                sizes="(min-width: 1480px) 1400px, (min-width: 992px) calc(100vw - 80px), calc(100vw - 40px)"
              />
            ) : null}
            {!!data.airtable.data.BannerTrans && (
              <span className="properties-banner f-big">
                {data.airtable.data.BannerTrans}
              </span>
            )}
          </div>
          <div className="row display-flex">
            <div className="col-md-8 main-property">
              <div className="brief proj">
                <h3 className="t-left f-normal ultrabold uppercase">
                  {data.descriptionText}:
                </h3>
                <div className="separator-small"></div>
                <p
                  className="t-left"
                  dangerouslySetInnerHTML={{
                    __html: [
                      data.airtable.data.BannerTrans
                        ? `<span class="properties-banner-inline">${data.airtable.data.BannerTrans}</span>`
                        : null,
                      data.airtable.data.DescriptionTrans,
                    ]
                      .filter(i => !!i)
                      .join(" "),
                  }}
                />
              </div>
              <div className="brief proj">
                <h3 className="t-left f-normal ultrabold uppercase">
                  {data.featuresText}:
                </h3>
                <div className="separator-small"></div>
                <ul className="t-left">
                  {data.airtable.data.FeaturesTrans.map((feature, k) => (
                    <li key={k}>{feature}</li>
                  ))}
                </ul>
                <div className="separator-small"></div>
                <p className="t-left f-small">
                  {data.locationText}:{" "}
                  <a
                    rel="noopener noreferrer"
                    target="_blank"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURI(
                      data.airtable.fields.locationTrans
                    )}`}
                    className="f-small normal"
                  >
                    {data.airtable.fields.locationTrans}
                  </a>
                </p>
                {data.airtable.data.Price ? (
                  <p className="t-left f-small">
                    {data.priceText}:{" "}
                    <span className="f-small bold a">
                      {data.airtable.data.PriceTrans} €
                    </span>
                  </p>
                ) : null}
              </div>
              <div id="images" className="brief for-images proj">
                {data.airtable.data.Images.localFiles
                  .slice()
                  .reverse()
                  .filter((img, k) => k !== 0)
                  .map((img, k) => (
                    <LightboxImage key={k} localFile={img} />
                  ))}
              </div>
            </div>
            <div className="col-md-4 contact-sidebar hidden-xs hidden-sm proj">
              <div id="property-contact">
                {data.patriciaPhoto && data.patriciaPhoto.childImageSharp ? (
                  <Image
                    className="property-contact-avatar"
                    fixed={data.patriciaPhoto.childImageSharp.fixed}
                    alt="Patricia Langkemper"
                  />
                ) : null}
                <p className="property-contact-name">
                  {data.contactName || "Patricia Langkemper"}
                </p>
                {data.interestedCtaText ? (
                  <p className="property-contact-title">
                    {data.interestedCtaText}
                  </p>
                ) : null}
                {data.contactTrustText ? (
                  <p className="property-contact-trust">
                    {data.contactTrustText}
                  </p>
                ) : null}
                <div className="property-contact-divider"></div>
                <div className="property-contact-buttons">
                  <a
                    href={emailHref}
                    onClick={copyEmail}
                    className={`btn btn-email-cta ${trackClass("Email")}`}
                  >
                    <i className="fa fa-envelope" aria-hidden="true"></i>{" "}
                    {emailLabel}
                    {emailCopied ? (
                      <span className="btn-copied-overlay">
                        <i className="fa fa-check" aria-hidden="true"></i>{" "}
                        {emailCopiedLabel}
                      </span>
                    ) : null}
                  </a>
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`btn btn-whatsapp-cta ${trackClass("WhatsApp")}`}
                    aria-label="WhatsApp"
                  >
                    <i className="fa fa-whatsapp" aria-hidden="true"></i>{" "}
                    {whatsappLabel}
                  </a>
                  <a
                    href={phoneHref}
                    className={`btn btn-phone-cta ${trackClass("Phone")}`}
                  >
                    <i className="fa fa-phone" aria-hidden="true"></i>{" "}
                    {data.contactPhoneText || "+34 637 889 111"}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        id="property-contact-mobile"
        className="btn-floating-cont hidden-md hidden-lg shown"
      >
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className={`btn btn-floating btn-whatsapp-cta ${trackClass(
            "WhatsApp"
          )}`}
          aria-label="WhatsApp"
        >
          <i className="fa fa-whatsapp" aria-hidden="true"></i>{" "}
          {whatsappLabelMobile}
        </a>
        <a
          href={phoneHref}
          className={`btn btn-floating btn-floating-icon btn-phone-cta ${trackClass(
            "Phone"
          )}`}
          aria-label="Call +34 637 889 111"
        >
          <i className="fa fa-phone" aria-hidden="true"></i>
        </a>
        <a
          href={emailHref}
          onClick={copyEmail}
          className={`btn btn-floating btn-floating-icon btn-email-cta ${trackClass(
            "Email"
          )}`}
          aria-label="Email"
        >
          <i
            className={`fa ${emailCopied ? "fa-check" : "fa-envelope"}`}
            aria-hidden="true"
          ></i>
        </a>
      </div>
    </Layout>
  )
}

export default PropertyTemplate

export const pageQuery = graphql`
  query PropertyTemplate($id: String!, $lang: String!) {
    descriptionText: getTranslation(lang: $lang, key: "property_description")
    featuresText: getTranslation(lang: $lang, key: "property_features")
    priceText: getTranslation(lang: $lang, key: "property_price")
    locationText: getTranslation(lang: $lang, key: "property_location")
    interestedCtaText: getTranslation(
      lang: $lang
      key: "property_interested_cta"
    )
    propertyWhatsappContactText: getTranslation(
      lang: $lang
      key: "property_whatsapp_contact_text"
    )
    contactWhatsappText: getTranslation(lang: $lang, key: "property_contact_whatsapp")
    contactWhatsappMobileText: getTranslation(lang: $lang, key: "property_contact_whatsapp_mobile")
    contactPhoneText: getTranslation(lang: $lang, key: "property_contact_phone")
    contactEmailText: getTranslation(lang: $lang, key: "property_contact_email")
    contactTrustText: getTranslation(lang: $lang, key: "property_contact_trust")
    contactName: getTranslation(lang: $lang, key: "property_contact_name")
    patriciaPhoto: file(relativePath: { eq: "patricia-langkemper.jpeg" }) {
      childImageSharp {
        fixed(width: 72, height: 72, quality: 80) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    airtable(id: { eq: $id }) {
      fields {
        locationTrans(lang: $lang)
        subtitleTrans(lang: $lang)
      }
      data {
        TitleTrans(lang: $lang)
        BannerTrans(lang: $lang)
        Location
        DescriptionTrans(lang: $lang)
        FeaturesTrans(lang: $lang)
        PriceTrans(lang: $lang)
        Reference
        SurfaceTrans(lang: $lang)
        BedroomsTrans(lang: $lang)
        FeaturedImage {
          childImageSharp {
            resize(width: 1200, height: 630) {
              src
            }
          }
        }
        Images {
          localFiles {
            childImageSharp {
              fluid(maxWidth: 1400, quality: 80) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
    }
  }
`
